import { z } from "zod/v4";

// ---------------------------------------------------------------------------
// Vocabularies (wire formats — every list below reaches storage, telemetry,
// or third-party rule code, so values are never renamed, only appended)
//
// Surface vocabulary lives in dtos/agentic-logic/surface instead — it is a
// field of AgenticLogicPlacementDto as well as of this envelope.
// ---------------------------------------------------------------------------

/**
 * Kinds of entity a result item can point at; `kind` scopes how `id` is
 * interpreted. Wire format — append-only. Consumers ignore kinds they don't
 * understand (never errors); the list exists so gates can validate rule
 * output.
 */
export const AgenticLogicItemKindList = [
  "product",
  "variant",
  "image",
  "reviewRef",
  "metaobjectRef",
  "bundle",
] as const;
export const AgenticLogicItemKindSchema = z.enum(AgenticLogicItemKindList);
export type AgenticLogicItemKind = (typeof AgenticLogicItemKindList)[number];

/**
 * What a result item asks the cart to do. Wire format — append-only.
 *
 * "replace" states the intent to swap out the originating cart line. NO
 * SURFACE MATERIALIZES IT TODAY — materializing a swap needs a cart transform
 * that removes the originating line, and none exists. The one statement of
 * which surface can do what is
 * `gates/renderable-kinds` AGENTIC_LOGIC_SURFACE_SUPPORTED_INTENTS; a rule
 * emitting an intent its target surfaces do not support is REFUSED at publish
 * rather than silently downgraded, because a surface that can only Add would
 * put the swap target alongside the item it was meant to replace.
 */
export const AgenticLogicItemIntentList = ["add", "replace"] as const;
export const AgenticLogicItemIntentSchema = z.enum(AgenticLogicItemIntentList);
export type AgenticLogicItemIntent =
  (typeof AgenticLogicItemIntentList)[number];

/** Discount shapes a result item can carry. Wire format — append-only. */
export const AgenticLogicItemDiscountTypeList = [
  "percentage",
  "fixed",
] as const;
export const AgenticLogicItemDiscountTypeSchema = z.enum(
  AgenticLogicItemDiscountTypeList
);
export type AgenticLogicItemDiscountType =
  (typeof AgenticLogicItemDiscountTypeList)[number];

/**
 * How trustworthy the shipping context is. Wire format — append-only.
 * "resolved" = known facts; "assumed" = best-effort defaults (no numeric
 * shipping copy allowed); "not-applicable" = nothing to reason about.
 */
export const AgenticLogicShippingStateList = [
  "resolved",
  "assumed",
  "not-applicable",
] as const;
export const AgenticLogicShippingStateSchema = z.enum(
  AgenticLogicShippingStateList
);
export type AgenticLogicShippingState =
  (typeof AgenticLogicShippingStateList)[number];

/**
 * Telemetry vocabulary for one rule evaluation — rides evaluation events,
 * NOT the result envelope. Wire format — append-only. Errors and timeouts
 * collapse to empty behaviorally (the surface follows the placement's
 * onEmpty policy) but stay distinct here.
 */
export const AgenticLogicEvaluationSourceList = [
  "serving",
  "api",
  "preview",
] as const;
export const AgenticLogicEvaluationSourceSchema = z.enum(
  AgenticLogicEvaluationSourceList
);
export type AgenticLogicEvaluationSource =
  (typeof AgenticLogicEvaluationSourceList)[number];

export const AgenticLogicEvaluationOutcomeList = [
  "result",
  "empty",
  "error",
] as const;
export const AgenticLogicEvaluationOutcomeSchema = z.enum(
  AgenticLogicEvaluationOutcomeList
);
export type AgenticLogicEvaluationOutcome =
  (typeof AgenticLogicEvaluationOutcomeList)[number];

/**
 * Which executor ran the rule. Wire format — append-only. Telemetry-side
 * vocabulary: it names the runtime lane, NOT the `version.executionKind`
 * storage column ("template" | "custom_lambda"). The two are deliberately
 * separate — a stored execution kind can gain new values without changing
 * what a readout groups by.
 */
export const AgenticLogicExecutionTierList = ["template", "custom"] as const;
export const AgenticLogicExecutionTierSchema = z.enum(
  AgenticLogicExecutionTierList
);
export type AgenticLogicExecutionTier =
  (typeof AgenticLogicExecutionTierList)[number];

/**
 * What the placement's onEmpty policy did with an empty rule result. Wire
 * format — append-only. This is what separates the empties a readout would
 * otherwise collapse: "not-applicable" = the rule returned items, so no
 * policy ran; "hidden" = onEmpty is "hide" and the surface renders nothing;
 * "materialized" = onEmpty is "smart-recs" and the fallback supplied items;
 * "unavailable" = smart-recs was asked and had nothing to give; "failed" =
 * the fallback read itself errored.
 */
export const AgenticLogicFallbackDispositionList = [
  "not-applicable",
  "hidden",
  "materialized",
  "unavailable",
  "failed",
] as const;
export const AgenticLogicFallbackDispositionSchema = z.enum(
  AgenticLogicFallbackDispositionList
);
export type AgenticLogicFallbackDisposition =
  (typeof AgenticLogicFallbackDispositionList)[number];

/**
 * Result-envelope schema version. Activating any reserved shape (candidates,
 * intent, discount, sellingPlanId) bumps this — consumers negotiate on it,
 * it never breaks silently. Independent of the REST API path version.
 */
export const AGENTIC_LOGIC_ENVELOPE_VERSION = 1;
